import { CollectionPath, db } from "Configs/firebase";
import { deleteUndefined } from "Helpers/FirestoreHelper";
import { ValidData } from "Store/types/ValidData";
import { Project, isProject } from "domain-entities";
import {
	collection,
	doc,
	getDoc,
	onSnapshot,
	query,
	setDoc,
	updateDoc,
	where,
} from "firebase/firestore";
import { create } from "zustand";

interface State {
	isInitializing: boolean;
	error?: Error;
	activeProjects: Project[];
	archivedProjects: Project[];
	projects: Project[];
	startedArchivedProjectIds: string[];
	subscribeToActiveProjects: (companyId?: string) => (() => void) | undefined;
	startFetchingArchivedProjects: (projectIds: string[]) => Promise<void>;
	updateProject: (project: Partial<Project> & ValidData) => Promise<void>;
	setProject: (project: Project) => Promise<void>;
	reset: () => void;
}

export const useProjects = create<State>((set, get) => ({
	isInitializing: true,
	activeProjects: [],
	archivedProjects: [],
	projects: [],
	startedArchivedProjectIds: [],
	subscribeToActiveProjects: (companyId) => {
		if (!companyId) return;

		return onSnapshot(
			query(
				collection(db, CollectionPath.PROJECTS),
				where("company", "==", companyId),
				where("status.name", "==", "active")
			),
			(snapshot) => {
				const activeProjects = snapshot.docs.reduce<Project[]>(
					(result, doc) => {
						const docData = doc.data();
						if (isProject(docData)) {
							result.push(docData);
						}
						return result;
					},
					[]
				);

				set({
					activeProjects,
					error: undefined,
					isInitializing: false,
					projects: [...activeProjects, ...get().archivedProjects],
				});
			},
			(error) => {
				set({ error, isInitializing: false });
			}
		);
	},

	/**
	 * Archived projects are not subscribed.
	 */
	startFetchingArchivedProjects: async (projectIds) => {
		const { startedArchivedProjectIds } = get();
		const notStartedProjectIds = projectIds.filter(
			(id) => !startedArchivedProjectIds.includes(id)
		);

		if (!notStartedProjectIds.length) return;
		get().startedArchivedProjectIds = [
			...startedArchivedProjectIds,
			...notStartedProjectIds,
		];

		const lazySnapshots = notStartedProjectIds.map((projectId) =>
			getDoc(doc(db, CollectionPath.PROJECTS, projectId))
		);

		const snapshots = await Promise.allSettled(lazySnapshots);
		const newArchivedProjects = snapshots.reduce<Project[]>(
			(result, snapshot) => {
				if (snapshot.status !== "fulfilled") return result;
				const project = snapshot.value.data();
				if (!isProject(project)) return result;
				return [...result, project];
			},
			[]
		);

		const archivedProjects = [
			...get().archivedProjects,
			...newArchivedProjects,
		];

		set({
			archivedProjects,
			projects: [...get().activeProjects, ...archivedProjects],
		});
	},
	updateProject: async (project) => {
		const previousProject = get().projects.find((p) => p.id === project.id);

		if (previousProject) {
			Object.assign(previousProject, project);
			set({ projects: [...get().projects] });
		}

		await updateDoc(
			doc(db, CollectionPath.PROJECTS, project.id),
			deleteUndefined(project, true)
		);
	},
	setProject: async (project) => {
		set({
			projects: [...get().projects, project],
			activeProjects: [...get().activeProjects, project],
		});

		await setDoc(
			doc(db, CollectionPath.PROJECTS, project.id),
			deleteUndefined(project)
		);
	},
	reset: () => {
		set({
			isInitializing: true,
			error: undefined,
			activeProjects: [],
			archivedProjects: [],
			projects: [],
			startedArchivedProjectIds: [],
		});
	},
}));
